"use client";

import { useTranslations } from "use-intl";
import { CounterButton } from "@/components/counter";
import { useCounterLocale } from "@/lib/i18n/CounterIntlProvider";
import { DEFAULT_LOCALE, LOCALE_SHORT, LOCALES, type CounterLocale } from "@/lib/i18n/config";

/**
 * One press between English and Hindi, from the rail's staff row and the phone's tab bar — not
 * only from Settings (founder, 2026-09-17).
 *
 * It sets the signed-in person's own choice, which is saved to their staff profile and follows them
 * to any till (`useCounterLocale().set`). The button shows the language the till is in now, in that
 * language's own script, so a cashier who cannot read the other one still recognises their own.
 */
export function LanguageQuickToggle({ variant }: { variant: "rail" | "tab" }) {
  const t = useTranslations("settings.language");
  const { locale, canChoose, set } = useCounterLocale();
  if (!canChoose) return null;

  const other: CounterLocale = LOCALES.find((id) => id !== locale) ?? DEFAULT_LOCALE;
  const label = t("quickLabel", { language: t(`names.${locale}`), other: t(`names.${other}`) });
  const mark = (
    <span lang={locale === "hi-IN" ? "hi" : "en"} className="font-sans text-sm font-bold leading-none">
      {LOCALE_SHORT[locale]}
    </span>
  );

  if (variant === "tab") {
    return (
      <CounterButton
        tone="on-rail"
        data-testid="pos-language-toggle"
        onClick={() => set(other)}
        aria-label={label}
        title={label}
        // A tab-bar item: the same 56px height, narrower than a destination because it is a control.
        className="min-h-[56px] w-14 shrink-0 rounded-none px-1"
      >
        {mark}
      </CounterButton>
    );
  }

  return (
    <CounterButton
      tone="on-rail"
      size="icon"
      data-testid="pos-language-toggle"
      onClick={() => set(other)}
      aria-label={label}
      title={label}
      className="shrink-0 rounded-tile"
    >
      {mark}
    </CounterButton>
  );
}
